import { Icon } from '@iconify/react';
import { useEffect, useRef, useState } from 'react';

import type { AvailableMint, CheckoutResponse, SwapToken } from '../api/checkout';
import {
  createSession,
  filterMintsForCheckout,
  getAvailableMints,
  getIndicativeQuote,
  getSession,
  getSwapTokens,
  mintKey,
} from '../api/checkout';
import type { PaymentOption } from '../utils/paymentOptions';
import {
  paymentOptionChainLabel,
  paymentOptionKey,
  paymentOptionSymbol,
} from '../utils/paymentOptions';
import { isValidOriginRefundAddress } from '../utils/refundAddress';
import NetworkTokenSelector from './NetworkTokenSelector';
import OrderSummaryRail from './OrderSummaryRail';

/**
 * Server error slug → payer copy. The slugs are stable; the words are ours,
 * and none of them explains how the payment is routed.
 */
const SUBMIT_ERROR_COPY: Record<string, string> = {
  invalid_refund_address:
    'That refund address was rejected. Please check it and try again.',
  refund_address_required: 'Please enter a refund address to continue.',
  unknown_origin_asset:
    'That token is no longer available. Please choose a different one.',
  token_not_available:
    'That token is not available for this payment. Please choose a different one.',
  amount_below_minimum:
    'This order is too small to pay with that token. Please choose a different one.',
  payment_method_unavailable:
    "We couldn't start your payment with that token right now. Please try again or choose a different one.",
  active_session_exists:
    'An active session already exists for this checkout. Please resume the existing session.',
};

const DEFAULT_SUBMIT_ERROR = "We couldn't start your payment. Please try again.";

export interface ProductViewProps {
  checkout: CheckoutResponse;
  onPaymentSession: (sessionId: string) => void;
}

export default function ProductView({ checkout, onPaymentSession }: ProductViewProps) {
  const [mints, setMints] = useState<AvailableMint[]>([]);
  const [extraTokens, setExtraTokens] = useState<SwapToken[]>([]);
  const [mintsLoading, setMintsLoading] = useState(true);
  const [mintsError, setMintsError] = useState(false);
  const [selected, setSelected] = useState<PaymentOption | null>(null);
  const [refundAddress, setRefundAddress] = useState('');
  const [refundTouched, setRefundTouched] = useState(false);
  const [quote, setQuote] = useState<number | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [quoteRetry, setQuoteRetry] = useState(0);
  const [focusSignal, setFocusSignal] = useState(0);
  const selectRef = useRef<HTMLSelectElement>(null);

  const priceUsd = Number(checkout.priceUsdSnapshot);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setMintsLoading(true);
      setMintsError(false);
      try {
        // `getSwapTokens` resolves to `[]` rather than rejecting, so it can
        // never turn an unavailable extra list into a failed page.
        const [allMints, tokens] = await Promise.all([
          getAvailableMints(),
          getSwapTokens(checkout.id),
        ]);
        const available = filterMintsForCheckout(
          allMints,
          checkout.acceptedNetworksTokensSnapshot,
        );
        if (cancelled) return;
        setMints(available);
        setExtraTokens(tokens);
        setSelected((current) => {
          if (available.length === 0) return null;
          if (current?.kind === 'mint') {
            const stillValid = available.find(
              (mint) => mintKey(mint) === mintKey(current.mint),
            );
            if (stillValid) return { kind: 'mint', mint: stillValid };
          }
          if (current?.kind === 'origin') {
            const stillValid = tokens.find(
              (token) => token.assetId === current.token.assetId,
            );
            if (stillValid) return { kind: 'origin', token: stillValid };
          }
          // The default is always a native mint: an eligible checkout still
          // opens on exactly the choice it offered before.
          return { kind: 'mint', mint: available[0] };
        });
      } catch {
        if (!cancelled) {
          setMints([]);
          setExtraTokens([]);
          setSelected(null);
          setMintsError(true);
        }
      } finally {
        if (!cancelled) setMintsLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [checkout.id, checkout.acceptedNetworksTokensSnapshot]);

  useEffect(() => {
    if (!selected) return undefined;
    // Only a native mint has an indicative USD rate to poll. An extra token's
    // amount comes from the session's own locked quote, so there is nothing to
    // show here and nothing to gate the Pay button on.
    if (selected.kind === 'origin') {
      setQuote(null);
      setQuoteLoading(false);
      setQuoteError(false);
      return undefined;
    }
    const mintId = selected.mint.mintId;
    if (mintId == null) {
      setQuote(null);
      setQuoteLoading(false);
      setQuoteError(true);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      if (!cancelled) setQuoteLoading(true);
      try {
        const q = await getIndicativeQuote(mintId);
        if (!cancelled) {
          setQuote(q);
          setQuoteError(false);
        }
      } catch {
        if (!cancelled) {
          setQuote(null);
          setQuoteError(true);
        }
      } finally {
        if (!cancelled) setQuoteLoading(false);
      }
    };
    void load();
    const id = window.setInterval(load, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [selected, quoteRetry]);

  const handlePay = async () => {
    if (!selected) return;
    if (selected.kind === 'mint' && selected.mint.mintId == null) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await createSession(
        checkout.id,
        selected.kind === 'mint'
          ? { mintId: selected.mint.mintId }
          : {
              origin_asset_id: selected.token.assetId,
              // Where funds go back to if the payment cannot be completed.
              refund_address: refundAddress.trim(),
            },
      );
      if (result.conflict) {
        const session = await getSession(checkout.id, result.activeSessionId);
        onPaymentSession(session.id);
        return;
      }
      onPaymentSession(result.session.id);
    } catch (e) {
      // Never surface raw server text: map the stable slug to fixed copy.
      const slug = e instanceof Error ? e.message : '';
      setSubmitError(SUBMIT_ERROR_COPY[slug] ?? DEFAULT_SUBMIT_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const originToken = selected?.kind === 'origin' ? selected.token : null;
  const refundValid =
    originToken !== null &&
    isValidOriginRefundAddress(originToken.blockchain, refundAddress);
  const showRefundError =
    originToken !== null && refundTouched && !refundValid;

  const payDisabled =
    submitting ||
    selected === null ||
    (selected.kind === 'mint'
      ? // Native: the indicative rate gates the button exactly as before.
        selected.mint.mintId == null || quoteLoading || quoteError || quote == null
      : // Extra token: no indicative rate, but a valid refund address is
        // required. The server re-validates before anything irreversible.
        !refundValid);

  return (
    <div className="ps-page">
      <div className="ps-shell">
        <div className="ps-body">
          <OrderSummaryRail checkout={checkout} />
          <section className="ps-main">
            <div className="ps-kicker">Pay with crypto</div>
            <div className="ps-title">${priceUsd.toFixed(2)} USD</div>
            {mintsLoading ? (
              <div className="ps-alert info">Loading payment methods...</div>
            ) : mintsError ? (
              <div className="ps-alert error">Could not load payment methods. Please refresh and try again.</div>
            ) : mints.length === 0 ? (
              <div className="ps-alert warning">No payment methods are configured for this link.</div>
            ) : (
              <>
                <NetworkTokenSelector
                  mints={mints}
                  extraTokens={extraTokens}
                  value={selected}
                  onChange={(option) => {
                    setSelected((current) =>
                      current && paymentOptionKey(current) === paymentOptionKey(option)
                        ? current
                        : option,
                    );
                    setRefundAddress('');
                    setRefundTouched(false);
                  }}
                  disabled={submitting}
                  selectRef={selectRef}
                  focusSignal={focusSignal}
                />
                {originToken ? (
                  <div style={{ marginTop: 16 }}>
                    <label
                      htmlFor="refund-address"
                      className="ps-kicker"
                      style={{ marginBottom: 8, display: 'block' }}
                    >
                      Refund address ({selected ? paymentOptionChainLabel(selected) : ''})
                    </label>
                    <input
                      id="refund-address"
                      className="ps-input"
                      type="text"
                      autoComplete="off"
                      spellCheck={false}
                      value={refundAddress}
                      disabled={submitting}
                      placeholder={`Your ${originToken.symbol} address`}
                      onChange={(e) => setRefundAddress(e.target.value)}
                      onBlur={() => setRefundTouched(true)}
                    />
                    <div
                      style={{
                        fontSize: 12,
                        color: showRefundError ? '#dc2626' : '#6b7280',
                        marginTop: 6,
                      }}
                    >
                      {showRefundError
                        ? `That does not look like a valid ${paymentOptionChainLabel(
                            selected as PaymentOption,
                          )} address.`
                        : `If your payment can't be completed, we'll return your ${originToken.symbol} to this address.`}
                    </div>
                  </div>
                ) : null}
                {quoteLoading ? (
                  <div className="ps-quote-pill">
                    <span className="ps-spinner" />
                    Fetching current rate...
                  </div>
                ) : quoteError ? (
                  <>
                    <div className="ps-quote-error">
                      <Icon icon="mdi:alert-outline" width={22} height={22} style={{ flexShrink: 0 }} />
                      <div>
                        <div className="ps-quote-error-title">Quote unavailable</div>
                        <div className="ps-quote-error-body">
                          We couldn&apos;t fetch a rate for{' '}
                          {selected ? paymentOptionSymbol(selected) : 'this token'}/USD right
                          now. This pair may be temporarily unavailable. Please try again or select a
                          different token.
                        </div>
                      </div>
                    </div>
                    <div className="ps-quote-actions">
                      <button
                        type="button"
                        className="ps-button neutral"
                        onClick={() => setQuoteRetry((n) => n + 1)}
                      >
                        Retry
                      </button>
                      <button
                        type="button"
                        className="ps-button outline"
                        onClick={() => {
                          selectRef.current?.focus();
                          setFocusSignal((n) => n + 1);
                        }}
                      >
                        Choose different token
                      </button>
                    </div>
                  </>
                ) : quote != null ? (
                  <div className="ps-quote-pill">
                    <Icon icon="mdi:clock-outline" width={18} height={18} style={{ flexShrink: 0 }} />
                    <span>
                      <span className="ps-quote-amount">
                        ≈ {(priceUsd / quote).toFixed(8)}{' '}
                        {selected ? paymentOptionSymbol(selected) : ''}
                      </span>{' '}
                      · refreshes every 30s
                    </span>
                  </div>
                ) : null}
                {submitError ? <div className="ps-alert error">{submitError}</div> : null}
                <div style={{ marginTop: 'auto' }}>
                  <button
                    type="button"
                    className="ps-button primary"
                    disabled={payDisabled}
                    onClick={() => void handlePay()}
                  >
                    Pay now
                  </button>
                  <div className="ps-secure" style={{ justifyContent: 'center', marginTop: 14 }}>
                    <Icon icon="mdi:lock-outline" width={16} height={16} />
                    Encrypted &amp; secure
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
